<script setup lang="ts">
/**
 * RinganTombol — padanan <CaButton> tanpa Nuxt UI.
 *
 * CaButton membungkus <UButton>, dan <UButton> menyeret reka-ui +
 * tailwind-variants ke setiap halaman yang memakainya. Untuk halaman kompres
 * itu ongkos yang tidak dibayar kembali: tombol di sana tidak memerlukan satu
 * pun perilaku istimewa milik library — hanya elemen <button> / <a> biasa.
 *
 * KELASNYA BUKAN KIRA-KIRA. Daftar di bawah disalin dari HTML hasil build
 * layout utama (setelah tailwind-merge menyelesaikan tabrakan antara kelas
 * bawaan UButton dan `ui.base` milik CaButton), lalu kelas semantik Nuxt UI
 * ditukar dengan padanan persisnya di design system kita:
 *
 *   text-default   → text-ink          (--ui-text        = --ca-text)
 *   bg-elevated    → bg-surface-2      (--ui-bg-elevated = --ca-surface-2)
 *   bg-default     → bg-surface        (--ui-bg          = --ca-surface)
 *   ring-accented  → ring-line-strong  (--ui-border-accented = --ca-line-strong)
 *   bg-primary     → bg-brand          (--ui-primary     = --ca-primary)
 *   text-primary   → text-brand        (idem)
 *   text-inverted  → text-brand-fg     (--ca-primary-fg: putih terang / gelap gelap)
 *
 * Semua pasangan itu berujung pada primitif --ca-* yang sama (lihat blok 2 & 3
 * di assets/css/v3.css), jadi warnanya identik di mode terang maupun gelap —
 * hanya saja tidak lagi lewat lapisan token Nuxt UI.
 *
 * CaButton ASLI TIDAK DISENTUH: halaman lain masih memakainya.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link' | 'emas'
type Size = 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    block?: boolean
    loading?: boolean
    disabled?: boolean
    /** Nama ikon Lucide di depan label (mis. 'i-lucide-download'). */
    icon?: string
    to?: string
    href?: string
    download?: string
    type?: 'button' | 'submit' | 'reset'
  }>(),
  { variant: 'primary', size: 'md', type: 'button' }
)

/** Ukuran → padding vertikal, ukuran teks, dan jarak ikon (dari UButton). */
const petaUkuran: Record<Size, string> = {
  sm: 'py-1.5 text-xs gap-1.5',
  md: 'py-1.5 text-sm gap-1.5',
  lg: 'py-2 text-sm gap-2'
}

/** Ukuran → tinggi minimum & padding horizontal (aturan design system CaButton). */
const petaTinggi: Record<Size, string> = {
  sm: 'min-h-9 px-3.5', // 36px — aksi sekunder padat saja
  md: 'min-h-11 px-5', //  44px — minimum untuk jempol
  lg: 'min-h-13 px-6' //   52px — CTA yang menghasilkan uang
}

const petaVarian: Record<Variant, string> = {
  primary: 'text-brand-fg bg-brand hover:bg-brand/75 active:bg-brand/75 disabled:bg-brand',
  secondary:
    'ring ring-inset ring-line-strong text-ink bg-surface hover:bg-surface-2 active:bg-surface-2 disabled:bg-surface',
  ghost: 'text-ink hover:bg-surface-2 active:bg-surface-2 hover:disabled:bg-transparent',
  danger: 'text-white bg-bad hover:bg-bad/75 active:bg-bad/75 disabled:bg-bad',
  link: 'text-brand hover:text-brand/75 active:text-brand/75 disabled:text-brand',
  // Warna sebenarnya datang dari `.ca-btn-emas` di assets/css/v3.css (gold cerah
  // sebagai LATAR, tinta gelap di atasnya). Di sini ia hanya menumpang bentuk solid.
  emas: 'text-brand-fg bg-brand ca-btn-emas'
}

const kelas = computed(() =>
  [
    'ca-focus rounded-control font-semibold',
    'items-center disabled:cursor-not-allowed disabled:opacity-75',
    'focus-visible:outline-3',
    'transition-transform duration-[120ms] ease-[cubic-bezier(0.2,0,0,1)] active:scale-[0.97]',
    petaUkuran[props.size],
    // Varian `link` tetap 44px tinggi (tap target) tapi rata kiri dan selebar
    // teksnya, supaya daftar tautan footer tidak jadi tombol raksasa yang melebar.
    props.variant === 'link'
      ? 'min-h-11 px-0 justify-start w-fit'
      : `justify-center ${petaTinggi[props.size]}`,
    props.block ? 'flex w-full' : 'inline-flex',
    petaVarian[props.variant]
  ].join(' ')
)

/** <a> dipakai untuk href eksternal/unduhan; NuxtLink untuk navigasi internal. */
const sebagai = computed(() => (props.to ? resolveComponent('NuxtLink') : props.href ? 'a' : 'button'))
</script>

<template>
  <component
    :is="sebagai"
    :to="to"
    :href="href"
    :download="download"
    :type="to || href ? undefined : type"
    :disabled="to || href ? undefined : disabled || loading"
    :aria-disabled="disabled || loading ? 'true' : undefined"
    :class="kelas"
  >
    <RinganIkon v-if="icon && !loading" :nama="icon" class="size-4 shrink-0" />
    <!-- Pemuatan ditandai lewat teks yang diganti pemanggil (mis. "Memproses…"),
         bukan spinner berputar: satu animasi kurang di HP kelas bawah, dan
         pembaca layar mendapat kabar yang sebenarnya, bukan gambar berputar. -->
    <slot />
  </component>
</template>
